import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

/**
 * Compact circular countdown ring showing time remaining on a pending
 * booking. Green > 5 min, amber 2-5 min, red under 2 min, grey when
 * expired. Ticks every second; consumers pass the ISO `expiresAt`
 * from the booking row.
 */
export default function BookingCountdown({ expiresAt, size = 44 }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!expiresAt) return null;
  const expiryMs = new Date(expiresAt).getTime();
  if (!Number.isFinite(expiryMs)) return null;

  const remainMs = Math.max(0, expiryMs - now);
  // Pending bookings live 30 minutes — use that as the ring baseline
  // so a fresh request paints full and drains counterclockwise.
  const totalMs = 30 * 60 * 1000;
  const progress = Math.min(1, Math.max(0, remainMs / totalMs));

  const totalSec = Math.round(remainMs / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  const label = remainMs === 0
    ? "0:00"
    : `${mm}:${ss.toString().padStart(2, "0")}`;

  const stroke = remainMs === 0
    ? "#94a3b8"
    : remainMs < 2 * 60 * 1000
      ? "#ef4444"
      : remainMs < 5 * 60 * 1000
        ? "#f59e0b"
        : "#10b981";

  // Ring stroke thickness scales with size so the big card variant
  // (104 px) still looks confident and the inline 44 px stays subtle.
  const ringWidth = size >= 80 ? 6 : 3;
  const r = (size - ringWidth * 2) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * progress;

  const fontSize = size >= 80 ? 18 : 10;
  const subLabelSize = size >= 80 ? 9 : 0;

  return (
    <div
      className="relative inline-flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
      data-testid="booking-countdown"
      title={remainMs === 0 ? "Request expired" : `Auto-cancels in ${label}`}
    >
      <svg width={size} height={size} className="rotate-[-90deg]">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#e5e7eb"
          strokeWidth={ringWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={stroke}
          strokeWidth={ringWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
          style={{ transition: "stroke-dasharray 0.6s linear, stroke 0.4s" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        {remainMs === 0 ? (
          <>
            <Clock size={size >= 80 ? 22 : 14} className="text-slate-400" />
            {size >= 80 && (
              <span className="mt-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                Expired
              </span>
            )}
          </>
        ) : (
          <>
            <span
              className="font-bold tabular-nums"
              style={{ color: stroke, fontSize }}
            >
              {label}
            </span>
            {size >= 80 && (
              <span
                className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-500"
                style={{ fontSize: subLabelSize }}
              >
                Until expiry
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
